<script lang="ts">
  import { createEventDispatcher } from "svelte";
  import { fade } from "svelte/transition";
  import Icon from "$lib/components/Icon/Icon.svelte";
  import Typography from "$lib/components/Typography/Typography.svelte";
  import DropdownItem from "../DropdownItem/DropdownItem.svelte";

  const dispatch = createEventDispatcher();

  export let label = "";
  export let selectedItem = "";
  export let dropdownOpen = false;
  export let disabled = false;
  export let capitalised = false;
  export let showInput = true;
  export let rounded = false;

  type Option = {
    label: string;
    section?: string;
    value?: string;
    countryCode?: string;
  };
  export let options: Option[];

  export let surfaceBackground = false;

  let activeIndex: number = -1;

  /**
   * Keydown event handler for the dropdown for wider accessibility support.
   * This function will handle keyboard navigation within the dropdown options.
   * @param event
   */
  const handleKeydown = (event: KeyboardEvent) => {
    //close if the user presses Escape
    if (event.key === "Escape") {
      event.preventDefault();
      dropdownOpen = false;
      activeIndex = -1; // Reset active index when closing
      return;
    }

    //Arrow keys for navigatio
    if (!dropdownOpen) {
      console.log({ key: event.key });
      if (
        event.key === "ArrowDown" ||
        event.key === "ArrowUp" ||
        event.key === "Enter" ||
        event.key === " "
      ) {
        event.preventDefault();
        dropdownOpen = true; // Open the dropdown if it's not already open
        activeIndex = 0; // Reset active index to the first option
      }
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      activeIndex = (activeIndex + 1) % options.length;
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      activeIndex = (activeIndex - 1 + options.length) % options.length;
    } else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (activeIndex >= 0 && activeIndex < options.length) {
        selectedItem = options[activeIndex].label;
        dropdownOpen = false;
        dispatch("itemSelected", {
          item: options[activeIndex].value
            ? options[activeIndex].value
            : options[activeIndex].label,
        });
      }
    }
  };
</script>

<style lang="scss">
  .dropdown {
    &--wrapper {
      display: flex;
      flex-direction: column;
      position: relative;
      width: 100%;
    }
    &--input {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      border: 1px solid $on-surface-10;
      padding: 13px 16px;
      cursor: pointer;

      &__rounded {
        border-radius: 24px;
        border: 1px solid rgba(0, 0, 0, 0.2);
      }

      &__surface {
        background: $surface;
        text-wrap-mode: nowrap;
      }

      &__disabled {
        cursor: default;
      }

      &__capitalised {
        text-transform: capitalize;
      }
    }

    &--options {
      position: absolute;
      top: 52px;
      left: 0;
      width: 100%;
      background-color: $surface;
      z-index: 10;
      border: 1px solid $on-surface-10;
      border-top: none;
      max-height: 265px;
      overflow: auto;

      &__rounded {
        border-bottom-right-radius: 24px;
        border-bottom-left-radius: 24px;
      }
    }

    &--padding {
      height: 26px;
      width: 100%;
      position: absolute;
      top: 26px;
      width: 100%;
      z-index: 10;
      border-left: 1px solid rgba(0, 0, 0, 0.1);
      border-right: 1px solid rgba(0, 0, 0, 0.1);
      background-color: transparent;
    }

    &--options-no-input {
      position: absolute;
      width: 100%;
      background-color: $surface;
      z-index: 10;
      border: 1px solid $on-surface-10;
      border-top: none;
      max-height: 265px;
      overflow: auto;
    }
  }

  @media screen and (max-height: 625px) {
    .dropdown {
      &--options {
        max-height: 25vh;
      }
    }
  }
</style>

{#if disabled}
  <div class="dropdown--wrapper">
    <div
      class={`dropdown--input dropdown--input__disabled ${
        capitalised ? "dropdown--input__capitalised" : ""
      } ${rounded ? "dropdown--input__rounded" : ""}`}
    >
      <Typography
        component="span"
        theme={selectedItem ? "on-background" : "on-surface-30"}
      >
        {selectedItem && capitalised
          ? selectedItem.toLowerCase()
          : selectedItem
            ? selectedItem
            : label}
      </Typography>
      <Icon icon="expand_more" color="var(--on-surface-10)" />
    </div>
  </div>
{:else}
  <!-- svelte-ignore a11y-no-static-element-interactions -->
  <div
    class="dropdown--wrapper"
    on:mouseleave={() =>
      showInput ? (dropdownOpen = false) : (dropdownOpen = true)}
  >
    {#if showInput}
      <div
        class={`dropdown--input ${rounded ? "dropdown--input__rounded" : ""} ${
          capitalised ? "dropdown--input__capitalised" : ""
        } ${surfaceBackground ? "dropdown--input__surface" : ""}`}
        role="button"
        aria-label={label}
        tabindex="0"
        on:click={() => (dropdownOpen = !dropdownOpen)}
        on:keydown={handleKeydown}
      >
        <Typography
          component="span"
          theme={selectedItem ? "on-background" : "on-surface-30"}
        >
          {selectedItem && capitalised
            ? selectedItem.toLowerCase()
            : selectedItem
              ? selectedItem
              : label}
        </Typography>
        <Icon
          icon={`${dropdownOpen === true ? "expand_less" : "expand_more"}`}
        />
      </div>
    {/if}

    {#if rounded && dropdownOpen}
      <div class="dropdown--padding" transition:fade={{ duration: 300 }} />
    {/if}

    {#if dropdownOpen}
      <div
        class={`dropdown--options${showInput ? "" : "-no-input"} ${
          rounded ? "dropdown--options__rounded" : ""
        }`}
        on:click={() => (dropdownOpen = false)}
        on:keydown={handleKeydown}
        transition:fade={{ duration: 300 }}
        role="listbox"
        tabindex="0"
        aria-hidden="false"
      >
        <!-- <slot /> -->
        {#each options as option, i (option.label)}
          <DropdownItem
            label={option.label}
            countryCode={option.countryCode}
            section={option.section}
            selected={selectedItem === option.label}
            highlighted={activeIndex === i}
            on:click={() => {
              selectedItem = option.label;
              dropdownOpen = false;
              dispatch("itemSelected", {
                item: option.value ? option.value : option.label,
              });
            }}
          />
        {/each}
      </div>
    {/if}
  </div>
{/if}
